import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaEye, FaEdit, FaBan, FaCheck, FaTimes, FaChevronLeft, FaChevronRight, FaUser } from 'react-icons/fa';
import { adminAPI } from '../../services/api';
import { getUploadUrl } from '../../utils/helpers';
import { toast } from 'react-toastify';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [selectedUserRoles, setSelectedUserRoles] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [assigningRole, setAssigningRole] = useState(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    is_vip: 'all',
    is_verified: 'all',
    page: 1,
    limit: 20
  });
  const [userRoles, setUserRoles] = useState({}); // Cache user roles

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await adminAPI.getUsers(filters);
      if (response.success) {
        setUsers(response.users);
        setPagination(response.pagination);

        // Fetch roles for all users (batch)
        const rolesPromises = response.users.map(user =>
          adminAPI.getUserRoles(user.id).catch(() => ({ user_roles: [] }))
        );
        const rolesResponses = await Promise.all(rolesPromises);
        const rolesMap = {};
        response.users.forEach((user, index) => {
          rolesMap[user.id] = rolesResponses[index].user_roles || [];
        });
        setUserRoles(rolesMap);
        setError(null);
      } else {
        setError(response.message);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      let errorMessage = 'Failed to load users';

      if (error.status === 401) {
        errorMessage = 'Unauthorized - Please login again';
      } else if (error.status === 403) {
        errorMessage = 'Access denied - Admin role required';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : 1 // Reset to page 1 when changing filters
    }));
  };

  const handleViewUser = async (userId) => {
    try {
      const response = await adminAPI.getUserDetails(userId);
      if (response.success) {
        setSelectedUser(response);
        setShowUserModal(true);
      }
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast.error('Failed to load user details');
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (userData) => {
    try {
      const response = await adminAPI.updateUser(selectedUser.user.id, userData);
      if (response.success) {
        toast.success('User updated successfully');
        setShowEditModal(false);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleBanUser = async (userId) => {
    if (!window.confirm('Are you sure you want to ban this user?')) return;

    try {
      const response = await adminAPI.banUser(userId, {
        reason: 'Admin action',
        duration_days: 30
      });
      if (response.success) {
        toast.success(response.message);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error banning user:', error);
      toast.error('Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      const response = await adminAPI.unbanUser(userId);
      if (response.success) {
        toast.success(response.message);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error unbanning user:', error);
      toast.error('Failed to unban user');
    }
  };

  const handleManageRoles = async (user) => {
    console.log('👥 Opening role management for user:', user.full_name, user.id);
    setSelectedUser(user);
    setSelectedUserRoles([]); // Clear previous roles
    setAvailableRoles([]); // Clear previous available roles
    setAssigningRole(null); // Reset loading state

    try {
      // Force fresh data load
      await Promise.all([
        fetchUserRoles(user.id),
        fetchAvailableRoles()
      ]);
      setShowRolesModal(true);
      console.log('✅ Role management modal opened successfully');
    } catch (error) {
      console.error('❌ Failed to load role data:', error);
      toast.error('Failed to load role management data');
    }
  };

  const fetchUserRoles = async (userId, bypassCache = false) => {
    try {
      console.log('👤 Fetching user roles for user:', userId, bypassCache ? '(bypassing cache)' : '');
      const config = bypassCache ? { headers: { 'Cache-Control': 'no-cache' } } : {};
      const response = await adminAPI.getUserRoles(userId, config);
      console.log('🔐 User roles response:', response);

      if (response && response.success) {
        setSelectedUserRoles(response.user_roles || []);
      } else {
        const errorMessage = response?.message || 'Failed to load user roles';
        console.error('❌ User roles API error:', errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error fetching user roles:', error);

      // Safe error object access
      const errorObj = error || {};
      console.error('Error details:', {
        message: errorObj.message,
        status: errorObj.response?.status,
        statusText: errorObj.response?.statusText,
        data: errorObj.response?.data,
        code: errorObj.code,
        name: errorObj.name
      });

      let errorMessage = 'Failed to load user roles';

      // Check for network errors
      if (errorObj.code === 'NETWORK_ERROR' ||
          errorObj.code === 'ERR_NETWORK' ||
          errorObj.message?.includes('Network Error') ||
          errorObj.message?.includes('Failed to fetch') ||
          !errorObj.response) {
        errorMessage = '🔌 Backend server is not running. Please start the backend server on port 5117';
      } else if (errorObj.response?.status === 401) {
        errorMessage = '🔐 Unauthorized - please login again as admin';
      } else if (errorObj.response?.status === 403) {
        errorMessage = '🚫 Access denied - admin privileges required';
      } else if (errorObj.response?.status === 500) {
        errorMessage = '🔧 Server error - please try again later';
      } else if (errorObj.response?.data?.message) {
        errorMessage = errorObj.response.data.message;
      } else if (errorObj.message) {
        errorMessage = `Error: ${errorObj.message}`;
      }

      toast.error(errorMessage);
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      console.log('👥 Fetching available roles');
      const response = await adminAPI.getRoles();
      console.log('🔐 Roles response:', response);

      if (response && response.success) {
        setAvailableRoles(response.roles || []);
      } else {
        const errorMessage = response?.message || 'Failed to load available roles';
        console.error('❌ Roles API error:', errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error fetching roles:', error);

      // Safe error object access
      const errorObj = error || {};
      console.error('Error details:', {
        message: errorObj.message,
        status: errorObj.response?.status,
        statusText: errorObj.response?.statusText,
        data: errorObj.response?.data,
        code: errorObj.code,
        name: errorObj.name
      });

      let errorMessage = 'Failed to load available roles';

      // Check for network errors
      if (errorObj.code === 'NETWORK_ERROR' ||
          errorObj.code === 'ERR_NETWORK' ||
          errorObj.message?.includes('Network Error') ||
          errorObj.message?.includes('Failed to fetch') ||
          !errorObj.response) {
        errorMessage = '🔌 Backend server is not running. Please start the backend server on port 5117';
      } else if (errorObj.response?.status === 401) {
        errorMessage = '🔐 Unauthorized - please login again as admin';
      } else if (errorObj.response?.status === 403) {
        errorMessage = '🚫 Access denied - admin privileges required';
      } else if (errorObj.response?.status === 500) {
        errorMessage = '🔧 Server error - please try again later';
      } else if (errorObj.response?.data?.message) {
        errorMessage = errorObj.response.data.message;
      } else if (errorObj.message) {
        errorMessage = `Error: ${errorObj.message}`;
      }

      toast.error(errorMessage);
    }
  };

  const handleAssignRole = async (roleId) => {
    if (assigningRole === roleId) return; // Prevent double-click

    setAssigningRole(roleId);
    try {
      const response = await adminAPI.assignUserRole(selectedUser.id, {
        role_id: roleId
      });

      if (response.success) {
        let successMessage = '✅ Role assigned successfully';

        if (response.action === 'updated') {
          successMessage = '🔄 Role updated successfully';
        } else if (response.action === 'no_change') {
          successMessage = 'ℹ️ Role was already active for this user';
        }

        if (response.requiresRelogin) {
          successMessage += ' User should re-login for changes to take effect.';
        }

        if (response.action === 'no_change') {
          toast.info(successMessage);
        } else {
          toast.success(successMessage);
        }

        // Always refresh data to show current state
        await fetchUserRoles(selectedUser.id);
        await fetchUsers();
      }
    } catch (error) {
      console.error('Error assigning role:', error);
      let errorMessage = 'Failed to assign role';

      // Handle specific error types
      if (error.response?.status === 404) {
        errorMessage = 'Role not found';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error - please try again';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage);
    } finally {
      setAssigningRole(null);
    }
  };

  const handleRemoveRole = async (roleId) => {
    try {
      const response = await adminAPI.removeUserRole(selectedUser.id, roleId);
      if (response.success) {
        let successMessage = 'Role removed successfully';

        if (response.requiresRelogin) {
          successMessage += ' User should re-login for changes to take effect.';
        }

        toast.success(successMessage);
        await fetchUserRoles(selectedUser.id);
        // Also refresh the main users list to show updated roles
        await fetchUsers();
      }
    } catch (error) {
      console.error('Error removing role:', error);
      toast.error(error.message || 'Failed to remove role');
    }
  };

  const handleBulkAssignRoles = async () => {
    const availableRoleIds = availableRoles
      .filter(role => !userRoles.some(userRole => userRole.role_id === role.id))
      .map(role => role.id);

    if (availableRoleIds.length === 0) {
      toast.info('ℹ️ All available roles are already assigned');
      return;
    }

    setBulkAssigning(true);
    try {
      const response = await adminAPI.bulkAssignUserRoles(selectedUser.id, {
        role_ids: availableRoleIds
      });

      if (response.success) {
        let successMessage = `✅ Successfully assigned ${response.assigned_count} roles`;

        if (response.requiresRelogin) {
          successMessage += ' User should re-login for changes to take effect.';
        }

        toast.success(successMessage);

        // Refresh data to show current state
        await fetchUserRoles(selectedUser.id);
        await fetchUsers();
      }
    } catch (error) {
      console.error('Error bulk assigning roles:', error);
      let errorMessage = 'Failed to bulk assign roles';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast.error(errorMessage);
    } finally {
      setBulkAssigning(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleDateString();
  };

  const UserModal = ({ user, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">User Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
              <div className="space-y-3">
                <div><strong>Name:</strong> {user.user.full_name}</div>
                <div><strong>Email:</strong> {user.user.email}</div>
                <div><strong>Age:</strong> {user.user.age}</div>
                <div><strong>Gender:</strong> {user.user.gender}</div>
                <div><strong>Location:</strong> {user.user.city}, {user.user.country}</div>
                <div><strong>VIP:</strong> {user.user.is_vip ? 'Yes' : 'No'}</div>
                <div><strong>Verified:</strong> {user.user.is_verified ? 'Yes' : 'No'}</div>
                <div><strong>Credits:</strong> {user.user.credits}</div>
              </div>
            </div>

            {/* Activity */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Activity</h3>
              <div className="space-y-3">
                <div><strong>Registered:</strong> {formatDate(user.user.registered)}</div>
                <div><strong>Last Login:</strong> {formatDate(user.user.last_login)}</div>
                <div><strong>Last Active:</strong> {formatDate(user.user.last_active)}</div>
                <div><strong>Bio:</strong> {user.user.bio || 'No bio'}</div>
              </div>
            </div>
          </div>

          {/* Photos */}
          {user.photos && user.photos.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Photos ({user.photos.length})</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {user.photos.map((photo) => (
                  <img
                    key={photo.id}
                    src={getUploadUrl(`user-${user.user.id}/${photo.path}`)}
                    alt="User photo"
                    className="w-full h-24 object-cover rounded-lg border"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Recent Transactions */}
          {user.transactions && user.transactions.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Recent Transactions</h3>
              <div className="space-y-2">
                {user.transactions.slice(0, 5).map((transaction) => (
                  <div key={transaction.id} className="flex justify-between p-2 bg-gray-50 rounded">
                    <span>{transaction.transaction_name}</span>
                    <span className={transaction.status === 1 ? 'text-green-600' : 'text-red-600'}>
                      ${transaction.transaction_amount}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const EditUserModal = ({ user, onClose, onSave }) => {
    const [formData, setFormData] = useState({
      full_name: user.user.full_name || '',
      email: user.user.email || '',
      credits: user.user.credits || 0,
      is_vip: user.user.is_vip || false,
      is_verified: user.user.is_verified || false,
      is_admin: user.user.is_admin || false
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full">
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Edit User</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Credits</label>
                <input
                  type="number"
                  value={formData.credits}
                  onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_vip}
                    onChange={(e) => setFormData({ ...formData, is_vip: e.target.checked })}
                    className="h-4 w-4 text-pink-600 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">VIP Status</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_verified}
                    onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                    className="h-4 w-4 text-pink-600 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Verified Status</label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_admin}
                    onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                    className="h-4 w-4 text-pink-600 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-700">Admin Status</label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-pink-600 text-white py-2 px-4 rounded-md hover:bg-pink-700"
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 lg:space-y-8 xl:space-y-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">Users Management</h1>
          <p className="text-gray-600 text-sm sm:text-base">Manage user accounts and permissions</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 lg:p-8 xl:p-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Name or email..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active (7 days)</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">VIP</label>
            <select
              value={filters.is_vip}
              onChange={(e) => handleFilterChange('is_vip', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="all">All Users</option>
              <option value="1">VIP Only</option>
              <option value="0">Non-VIP Only</option>
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Verified</label>
            <select
              value={filters.is_verified}
              onChange={(e) => handleFilterChange('is_verified', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
            >
              <option value="all">All Users</option>
              <option value="1">Verified Only</option>
              <option value="0">Unverified Only</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchUsers}
              className="w-full bg-pink-600 text-white py-2 px-3 sm:px-4 lg:px-6 xl:px-8 rounded-md hover:bg-pink-700 text-sm lg:text-base xl:text-lg font-medium transition-colors flex items-center justify-center"
            >
              <FaFilter className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Apply Filters</span>
              <span className="sm:hidden">Filter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32 sm:h-64">
            <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-pink-500"></div>
          </div>
        ) : error ? (
          <div className="p-4 sm:p-6 text-center text-red-600 text-sm">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] lg:min-w-[800px] xl:min-w-[1000px] 2xl:max-w-none">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VIP</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roles</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center min-w-0">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{user.full_name}</div>
                            <div className="text-xs text-gray-500 truncate">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className={`inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full ${
                          user.is_verified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          <span className="hidden sm:inline">{user.is_verified ? 'Verified' : 'Unverified'}</span>
                          <span className="sm:hidden">{user.is_verified ? '✓' : '✗'}</span>
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        {user.is_vip ? (
                          <span className="inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            VIP
                          </span>
                        ) : (
                          <span className="text-xs sm:text-sm text-gray-500">No</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        {user.credits}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1 max-w-32 sm:max-w-none">
                          {userRoles[user.id] && userRoles[user.id].length > 0 ? (
                            userRoles[user.id].slice(0, 2).map((role) => (
                              <span
                                key={role.id}
                                className={`inline-flex px-1 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full ${
                                  role.role_name === 'admin' ? 'bg-red-100 text-red-800' :
                                  role.role_name === 'moderator' ? 'bg-blue-100 text-blue-800' :
                                  role.role_name === 'premium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}
                              >
                                {role.role_display_name}
                              </span>
                            ))
                          ) : (
                            <span className="inline-flex px-1 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                              No Roles
                            </span>
                          )}
                          {userRoles[user.id] && userRoles[user.id].length > 2 && (
                            <span className="inline-flex px-1 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                              +{userRoles[user.id].length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                        {formatDate(user.last_active)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => handleViewUser(user.id)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded transition-colors"
                            title="View Details"
                          >
                            <FaEye className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={() => handleEditUser({ user })}
                            className="text-yellow-600 hover:text-yellow-900 p-1 rounded transition-colors"
                            title="Edit User"
                          >
                            <FaEdit className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={() => handleManageRoles(user)}
                            className="text-purple-600 hover:text-purple-900 p-1 rounded transition-colors"
                            title="Manage Roles"
                          >
                            <FaUser className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                          {user.is_verified ? (
                            <button
                              onClick={() => handleBanUser(user.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                              title="Ban User"
                            >
                              <FaBan className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleUnbanUser(user.id)}
                              className="text-green-600 hover:text-green-900 p-1 rounded transition-colors"
                              title="Unban User"
                            >
                              <FaCheck className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-3 sm:px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="text-xs sm:text-sm text-gray-700 text-center sm:text-left">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex items-center justify-center sm:justify-end space-x-1 sm:space-x-2">
                    <button
                      onClick={() => handleFilterChange('page', pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>

                    <span className="relative inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-700">
                      {pagination.page} / {pagination.totalPages}
                    </span>

                    <button
                      onClick={() => handleFilterChange('page', pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showUserModal && selectedUser && (
        <UserModal
          user={selectedUser}
          onClose={() => setShowUserModal(false)}
        />
      )}

      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateUser}
        />
      )}

      {/* Roles Modal */}
      {showRolesModal && selectedUser && (
        <RolesModal
          user={selectedUser}
          userRoles={selectedUserRoles}
          availableRoles={availableRoles}
          onClose={() => setShowRolesModal(false)}
          onAssignRole={handleAssignRole}
          onRemoveRole={handleRemoveRole}
          onBulkAssignRoles={handleBulkAssignRoles}
          assigningRole={assigningRole}
          bulkAssigning={bulkAssigning}
        />
      )}
    </div>
  );
};

const RolesModal = ({ user, userRoles, availableRoles, onClose, onAssignRole, onRemoveRole, onBulkAssignRoles, assigningRole, bulkAssigning }) => {
  const getRoleBadgeColor = (roleName) => {
    switch (roleName) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'moderator': return 'bg-blue-100 text-blue-800';
      case 'premium': return 'bg-yellow-100 text-yellow-800';
      case 'user': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const availableRolesToAssign = availableRoles.filter(role =>
    !userRoles.some(userRole => parseInt(userRole.role_id) === parseInt(role.id))
  );

  // Debug logging
  console.log('🔍 Role filtering debug:', {
    userRoles: userRoles.map(r => ({ id: r.role_id, name: r.role_name })),
    availableRoles: availableRoles.map(r => ({ id: r.id, name: r.name })),
    availableToAssign: availableRolesToAssign.map(r => ({ id: r.id, name: r.name }))
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                Manage Roles
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                User: <span className="font-medium">{user.full_name}</span> (ID: {user.id})
                {user.email && <span className="ml-2">• {user.email}</span>}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          {/* Current Roles */}
          <div className="mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-semibold mb-3">
              Current Roles ({userRoles.length})
            </h3>
            {userRoles.length === 0 ? (
              <div className="text-center py-8">
                <FaUser className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No roles assigned to this user</p>
                <p className="text-xs text-gray-400 mt-1">Use "Assign New Role" section below to add roles</p>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned By
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expires
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userRoles.map((role) => (
                        <tr key={role.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(role.role_name)}`}>
                                {role.role_display_name}
                              </span>
                              {role.role_description && (
                                <span className="ml-2 text-xs text-gray-500 truncate max-w-32" title={role.role_description}>
                                  {role.role_description}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {new Date(role.assigned_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {role.assigned_by_name || `Admin ID: ${role.assigned_by}`}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {role.expires_at ? new Date(role.expires_at).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => onRemoveRole(role.role_id)}
                              className="text-red-600 hover:text-red-900 px-3 py-1.5 text-xs border border-red-200 rounded hover:bg-red-50 transition-colors"
                              disabled={userRoles.length === 1 && role.role_name === 'user'}
                              title={userRoles.length === 1 && role.role_name === 'user' ? 'Cannot remove the last user role' : 'Remove role'}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Assign New Role */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base sm:text-lg font-semibold">
                Assign New Role
                {availableRolesToAssign.length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {availableRolesToAssign.length} available
                  </span>
                )}
              </h3>
              {availableRolesToAssign.length > 1 && (
                <button
                  onClick={onBulkAssignRoles}
                  disabled={bulkAssigning}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    bulkAssigning
                      ? 'bg-green-400 text-white cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {bulkAssigning ? 'Assigning...' : `Assign All (${availableRolesToAssign.length})`}
                </button>
              )}
            </div>
            {availableRolesToAssign.length === 0 ? (
              <p className="text-gray-500 italic text-sm">All available roles are already assigned</p>
            ) : (
              <div className="space-y-2">
                {availableRolesToAssign.map((role) => (
                  <div key={role.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border border-gray-200 rounded-lg gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm sm:text-base">{role.display_name}</div>
                      <div className="text-xs sm:text-sm text-gray-500 truncate">{role.description}</div>
                    </div>
                    <button
                      onClick={() => onAssignRole(role.id)}
                      disabled={assigningRole === role.id}
                      className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors self-start sm:self-center flex-shrink-0 ${
                        assigningRole === role.id
                          ? 'bg-blue-400 text-white cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      title="Assign or update this role for the user"
                    >
                      {assigningRole === role.id ? 'Processing...' : 'Assign/Update'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end mt-4 sm:mt-6">
            <button
              onClick={onClose}
              className="bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;